import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircleIcon, LockClosedIcon, PlayCircleIcon } from '@heroicons/react/24/solid'
import { ListBulletIcon, ShareIcon } from '@heroicons/react/24/outline'
import TimeEstimateBadge from './TimeEstimateBadge'
import BookmarkButton from './BookmarkButton'
import PrerequisiteTree from './PrerequisiteTree'

export default function LearningPath({ modules = [], currentModuleId = null }) {
  const [viewMode, setViewMode] = useState('list') // 'list' or 'tree'
  
  const getModuleStatus = (module) => {
    if (module.progress?.status === 'completed') return 'completed'
    if (module.progress?.status === 'in_progress') return 'in-progress'
    if (module.id === currentModuleId) return 'current'
    
    // Check if previous modules are completed (unlock logic)
    const currentIndex = modules.findIndex(m => m.id === module.id)
    if (currentIndex === 0) return 'available'
    
    const previousModule = modules[currentIndex - 1]
    if (previousModule?.progress?.status === 'completed') return 'available'
    
    return 'locked'
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-8 w-8 text-green-500" />
      case 'in-progress':
      case 'current':
        return <PlayCircleIcon className="h-8 w-8 text-blue-500 animate-pulse" />
      case 'available':
        return <div className="h-8 w-8 rounded-full border-4 border-blue-300 bg-white" />
      case 'locked':
      default:
        return <LockClosedIcon className="h-8 w-8 text-gray-300" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 border-green-500 text-green-900'
      case 'in-progress':
      case 'current':
        return 'bg-blue-100 border-blue-500 text-blue-900 ring-4 ring-blue-200'
      case 'available':
        return 'bg-white border-blue-300 text-gray-900 hover:border-blue-500 hover:shadow-md'
      case 'locked':
      default:
        return 'bg-gray-50 border-gray-200 text-gray-400'
    }
  }

  return (
    <div className="relative">
      {/* View Mode Toggle */}
      <div className="flex justify-end mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-cte-blue text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ListBulletIcon className="h-4 w-4" />
            List View
          </button>
          <button
            onClick={() => setViewMode('tree')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'tree'
                ? 'bg-cte-blue text-white'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ShareIcon className="h-4 w-4" />
            Prerequisite Tree
          </button>
        </div>
      </div>

      {/* Tree View */}
      {viewMode === 'tree' && (
        <div>
          <PrerequisiteTree />
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>💡 Tip:</strong> The prerequisite tree shows your learning path. 
              Green modules are complete, blue are available to start, and gray are locked 
              until you complete their prerequisites. Click and drag to explore!
            </p>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-8">
          {modules.map((module, index) => {
          const status = getModuleStatus(module)
          const isLocked = status === 'locked'
          const isClickable = !isLocked

          const CardContent = (
            <div
              className={`relative flex items-center p-4 rounded-lg border-2 transition-all ${getStatusColor(status)} ${
                isClickable ? 'cursor-pointer transform hover:scale-105' : 'cursor-not-allowed'
              }`}
            >
              {/* Connection line to previous module */}
              {index > 0 && (
                <div className="absolute -top-8 left-8 w-0.5 h-8 bg-gray-300" />
              )}

              {/* Module icon/status */}
              <div className="flex-shrink-0">
                {getStatusIcon(status)}
              </div>

              {/* Module info */}
              <div className="ml-4 flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className="text-xs font-semibold uppercase tracking-wide opacity-75">
                      {module.code}
                    </span>
                    <h4 className={`font-bold ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>
                      {module.title}
                    </h4>
                    {module.estimated_minutes && (
                      <div className="mt-1">
                        <TimeEstimateBadge 
                          minutes={module.estimated_minutes} 
                          className={isLocked ? 'bg-gray-100 text-gray-500' : ''} 
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {!isLocked && (
                      <BookmarkButton type="Module" id={module.id} size="sm" />
                    )}
                    {module.progress?.completion_percentage > 0 && (
                      <span className="text-sm font-semibold">
                        {module.progress.completion_percentage}%
                      </span>
                    )}
                  </div>
                </div>
                {status === 'in-progress' && (
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${module.progress?.completion_percentage || 0}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Lock overlay for locked modules */}
              {isLocked && (
                <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <LockClosedIcon className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                    <span className="text-xs text-gray-500">Complete previous module</span>
                  </div>
                </div>
              )}
            </div>
          )

          return (
            <div key={module.id}>
              {isClickable ? (
                <Link to={`/modules/${module.id}`}>
                  {CardContent}
                </Link>
              ) : (
                CardContent
              )}
            </div>
          )
        })}
      </div>
      )}
    </div>
  )
}
